// frontend/src/components/ResetPassword.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import './Login.css';

const ResetPassword = () => {
  const { uid, token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [username, setUsername] = useState('');

  const [passwords, setPasswords] = useState({
    password: '',
    password2: ''
  });

  // 👇 NUEVO: Estados para mostrar/ocultar contraseñas
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);

  // 👇 NUEVO: Estado para fortaleza de contraseña
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: '',
    color: '#e0e0e0'
  });

  // Validar token al cargar
  useEffect(() => {
    validateToken();
  }, [uid, token]);

  // 👇 NUEVO: Evaluar fortaleza cuando cambia la contraseña
  useEffect(() => {
    if (passwords.password) {
      evaluatePasswordStrength(passwords.password);
    } else {
      setPasswordStrength({ score: 0, feedback: '', color: '#e0e0e0' });
    }
  }, [passwords.password]);

  const validateToken = async () => {
    try {
      const response = await api.post('/auth/validate-reset-token/', {
        uid,
        token
      });

      if (response.data.valid) {
        setTokenValid(true);
        setUsername(response.data.username || '');
      } else {
        setError(response.data.error || 'Este enlace ha expirado o es inválido');
      }
    } catch (err) {
      console.error('Error validando token:', err);
      setError('Error al validar el enlace. Por favor, solicita uno nuevo.');
    } finally {
      setValidating(false);
    }
  };

  // 👇 NUEVA FUNCIÓN: Evaluar fortaleza de la contraseña
  const evaluatePasswordStrength = (password) => {
    let score = 0;
    let feedback = '';
    let color = '#e0e0e0';

    // Evaluar longitud
    if (password.length >= 6) score++;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;

    // Evaluar complejidad
    if (/[a-z]/.test(password)) score++; // minúsculas
    if (/[A-Z]/.test(password)) score++; // mayúsculas
    if (/[0-9]/.test(password)) score++; // números
    if (/[^A-Za-z0-9]/.test(password)) score++; // símbolos

    // Determinar feedback y color
    if (score <= 2) {
      feedback = '🔴 Débil';
      color = '#e74c3c';
    } else if (score <= 4) {
      feedback = '🟡 Media';
      color = '#f39c12';
    } else if (score <= 5) {
      feedback = '🟢 Fuerte';
      color = '#27ae60';
    } else {
      feedback = '🟢 Muy fuerte';
      color = '#2ecc71';
    }

    setPasswordStrength({ score, feedback, color });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validaciones
    if (!passwords.password || !passwords.password2) {
      setError('Por favor completa todos los campos');
      return;
    }

    if (passwords.password !== passwords.password2) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (passwords.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/auth/password-reset-confirm/', {
        uid,
        token,
        password: passwords.password
      });

      if (response.data.success) {
        setSuccess('✅ Contraseña actualizada exitosamente. Redirigiendo...');
        setPasswords({ password: '', password2: '' });

        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } catch (err) {
      console.error('Error al actualizar contraseña:', err);
      setError(err.response?.data?.error || 'Error al actualizar la contraseña');
    } finally {
      setLoading(false);
    }
  };

  // Pantalla de validación
  if (validating) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div style={{ padding: '60px 40px', textAlign: 'center' }}>
            <div style={{
              fontSize: '3rem',
              marginBottom: '20px',
              animation: 'spin 1s linear infinite'
            }}>
              🔄
            </div>
            <h2 style={{ color: '#69B32D' }}>Validando enlace...</h2>
            <p style={{ color: '#666', marginTop: '10px' }}>Por favor espera un momento</p>
          </div>
        </div>
      </div>
    );
  }

  // Pantalla de token inválido
  if (!tokenValid) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div style={{ padding: '60px 40px', textAlign: 'center' }}>
            <div style={{ fontSize: '4rem', marginBottom: '20px' }}>❌</div>
            <h2 style={{ color: '#e74c3c', marginBottom: '15px' }}>
              Enlace Inválido o Expirado
            </h2>
            <p className="subtitle" style={{ marginBottom: '25px' }}>
              {error || 'Este enlace ya no es válido. Por favor, solicita uno nuevo.'}
            </p>
            <div className="info-text" style={{ marginBottom: '25px' }}>
              💡 Los enlaces de recuperación expiran después de 24 horas por seguridad.
            </div>
            <button
              className="btn-submit"
              onClick={() => navigate('/login')}
            >
              Volver al Login
            </button>
            <div className="back-to-login" style={{ marginTop: '15px' }}>
              <button
                type="button"
                className="link-button"
                onClick={() => navigate('/login')}
              >
                ¿Necesitas un nuevo enlace? Solicítalo aquí
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Formulario principal
  return (
    <div className="login-container">
      <div className="login-card">
        <form onSubmit={handleSubmit} style={{ padding: '40px' }}>
          <div style={{ textAlign: 'center', marginBottom: '25px' }}>
            <div style={{ fontSize: '3rem', marginBottom: '10px' }}>🔑</div>
            <h2>Nueva Contraseña</h2>
            {username && (
              <p className="subtitle">
                Para: <strong>{username}</strong>
              </p>
            )}
          </div>

          {/* Campo Nueva Contraseña con botón mostrar/ocultar */}
          <div className="form-group">
            <label>Nueva Contraseña *</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={passwords.password}
                onChange={(e) => setPasswords({ ...passwords, password: e.target.value })}
                placeholder="Mínimo 6 caracteres"
                required
                disabled={loading}
                style={{ paddingRight: '45px' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1.2rem',
                  padding: '5px',
                  opacity: 0.7
                }}
                title={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>

            {/* 👇 NUEVO: Indicador de fortaleza */}
            {passwords.password && (
              <div style={{ marginTop: '10px' }}>
                <div style={{
                  height: '6px',
                  backgroundColor: '#e0e0e0',
                  borderRadius: '3px',
                  overflow: 'hidden',
                  marginBottom: '5px'
                }}>
                  <div
                    style={{
                      height: '100%',
                      width: `${(passwordStrength.score / 7) * 100}%`,
                      backgroundColor: passwordStrength.color,
                      transition: 'all 0.3s ease'
                    }}
                  ></div>
                </div>
                <p style={{
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  margin: 0
                }}>
                  {passwordStrength.feedback}
                </p>
              </div>
            )}
          </div>

          {/* Campo Confirmar Contraseña con botón mostrar/ocultar */}
          <div className="form-group">
            <label>Confirmar Contraseña *</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword2 ? 'text' : 'password'}
                value={passwords.password2}
                onChange={(e) => setPasswords({ ...passwords, password2: e.target.value })}
                placeholder="Repite la contraseña"
                required
                disabled={loading}
                style={{ paddingRight: '45px' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword2(!showPassword2)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1.2rem',
                  padding: '5px',
                  opacity: 0.7
                }}
                title={showPassword2 ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              >
                {showPassword2 ? '🙈' : '👁️'}
              </button>
            </div>

            {/* 👇 NUEVO: Indicador de coincidencia */}
            {passwords.password2 && (
              <p style={{
                fontSize: '0.85rem',
                marginTop: '8px',
                fontWeight: '600',
                color: passwords.password === passwords.password2 ? '#27ae60' : '#e74c3c'
              }}>
                {passwords.password === passwords.password2
                  ? '✓ Las contraseñas coinciden'
                  : '✗ Las contraseñas no coinciden'}
              </p>
            )}
          </div>

          {/* 👇 NUEVO: Recomendaciones */}
          <div className="info-text" style={{ fontSize: '0.8rem', marginBottom: '20px' }}>
            <strong>💡 Recomendaciones:</strong>
            <ul style={{
              marginTop: '8px',
              paddingLeft: '20px',
              textAlign: 'left',
              lineHeight: '1.6'
            }}>
              <li>Usa al menos 8 caracteres</li>
              <li>Combina mayúsculas y minúsculas</li>
              <li>Incluye números y símbolos</li>
              <li>Evita información personal</li>
            </ul>
          </div>

          {error && <div className="error-message">❌ {error}</div>}
          {success && <div className="success-message">{success}</div>}

          <button
            type="submit"
            disabled={loading || (passwords.password2 && passwords.password !== passwords.password2)}
            className="btn-submit"
          >
            {loading ? 'Actualizando...' : 'Actualizar Contraseña'}
          </button>

          <div className="back-to-login">
            <button
              type="button"
              className="link-button"
              onClick={() => navigate('/login')}
              disabled={loading}
            >
              ← Volver al inicio de sesión
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;